/**
 * useTurnBoundary - 弯边界检测 Hook
 *
 * Provides turn-boundary lookup utilities and a memoized React hook for
 * determining which turn (if any) contains a given timestamp, plus the
 * next/previous turn boundary in a given direction.
 *
 * 提供弯边界查找工具与 memoized React Hook，用于判断时间戳落在哪个弯内，
 * 以及沿指定方向查找上一个/下一个弯边界。
 *
 * The turns array is assumed to be sorted ascending by `start_ms` and
 * non-overlapping (per AR-1 turn detection output).
 * 弯数组按 `start_ms` 升序排列且不重叠（对应 AR-1 输出）。
 */

import { useMemo } from 'react';
import type { Turn } from '../types/ski-data';

/**
 * Find the turn that contains `timestampMs` within its [start_ms, end_ms].
 *
 * 查找 `timestampMs` 落在其 [start_ms, end_ms] 区间内的弯。
 *
 * @param turns        - non-overlapping turns sorted by start_ms / 不重叠的弯数组
 * @param timestampMs  - target timestamp in ms / 目标时间戳（毫秒）
 * @returns 1-based `turn_index` if inside a turn, otherwise `null`
 *          若落在某弯区间内返回其 1-based turn_index，否则返回 null
 */
export function findTurnAtTimestamp(
  turns: Turn[],
  timestampMs: number
): number | null {
  for (const turn of turns) {
    if (timestampMs >= turn.start_ms && timestampMs <= turn.end_ms) {
      return turn.turn_index;
    }
  }
  return null;
}

/**
 * Find the next turn boundary in the given direction.
 *
 * - direction = 1:  first turn whose `start_ms` is strictly greater than
 *   `currentTimestampMs` (the upcoming turn).
 * - direction = -1: last turn whose `end_ms` is strictly less than
 *   `currentTimestampMs` (the previous turn).
 *
 * 沿指定方向查找下一个弯边界。
 *
 * @param turns               - non-overlapping turns sorted by start_ms
 * @param currentTimestampMs  - reference timestamp / 参考时间戳
 * @param direction           - 1 = forward, -1 = backward / 1=向前，-1=向后
 * @returns the next turn in the given direction, or `null` if none exists
 */
export function findNextTurn(
  turns: Turn[],
  currentTimestampMs: number,
  direction: 1 | -1
): Turn | null {
  if (direction === 1) {
    // Forward: first turn starting after the current timestamp.
    // 向前：首个 start_ms 大于当前时间戳的弯。
    for (const turn of turns) {
      if (turn.start_ms > currentTimestampMs) return turn;
    }
  } else {
    // Backward: last turn ending before the current timestamp.
    // 向后：最后一个 end_ms 小于当前时间戳的弯。
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].end_ms < currentTimestampMs) return turns[i];
    }
  }
  return null;
}

/**
 * Memoized turn-boundary hook. Recomputes only when `turns` or `timestampMs`
 * change, returning the current 1-based turn index and the full Turn object.
 *
 * Memoized 弯边界 Hook。仅在 `turns` 或 `timestampMs` 变化时重新计算，
 * 返回当前 1-based 弯序号及完整 Turn 对象。
 *
 * @param turns        - non-overlapping turns sorted by start_ms
 * @param timestampMs  - target timestamp in ms / 目标时间戳
 * @returns `{ currentTurnIndex, currentTurn }` — both null when between turns
 */
export function useTurnBoundary(
  turns: Turn[],
  timestampMs: number
): { currentTurnIndex: number | null; currentTurn: Turn | null } {
  const currentTurnIndex = useMemo(
    () => findTurnAtTimestamp(turns, timestampMs),
    [turns, timestampMs]
  );

  const currentTurn = useMemo(() => {
    if (currentTurnIndex == null) return null;
    return turns.find((t) => t.turn_index === currentTurnIndex) ?? null;
  }, [turns, currentTurnIndex]);

  return { currentTurnIndex, currentTurn };
}
